import { createLibrary, defineComponent } from "@openuidev/react-lang";
import { parentInsightLibrary } from "@tutor/genui";
import type { ProgressReport } from "@tutor/protocol";
import {
  AlertCountFigure,
  InsightCard as InsightCardView,
  SkillProgressFigure,
  type InsightTone,
} from "@tutor/ui/parent";
import { createContext, useContext } from "react";
import { z } from "zod";
import { useLocale } from "../i18n";
import { readString, type WidgetProps } from "../widgets/props";
import type { AlertKind } from "./ranking";
import { skillLabel } from "./skill-label";

// Parent insight cards rendered with the design-system components from `@tutor/ui/parent`
// (self-hosted OpenUI, ADR-0014). Names, schemas and descriptions come from `@tutor/genui`;
// the program names skills and metrics only, and every figure is looked up here in the
// engine's report.

export interface Figures {
  readonly report: ProgressReport;
  readonly alertCounts: ReadonlyMap<AlertKind, number>;
}

export const FiguresContext = createContext<Figures | null>(null);

interface SharedDefinition {
  readonly props: z.ZodObject;
  readonly description: string;
}

function shared(name: string): SharedDefinition {
  const entry: unknown = parentInsightLibrary().components[name];
  if (
    typeof entry === "object" &&
    entry !== null &&
    "props" in entry &&
    entry.props instanceof z.ZodObject &&
    "description" in entry &&
    typeof entry.description === "string"
  ) {
    return { props: entry.props, description: entry.description };
  }
  throw new Error(`@tutor/genui has no parent card named ${name}`);
}

function SkillFigure({ props }: { props: WidgetProps }) {
  const figures = useContext(FiguresContext);
  const locale = useLocale();
  const skillId = readString(props, "skillId");
  const metric = readString(props, "metric");
  const row = figures?.report.skills.find((s) => s.skillId === skillId);
  // No report row, no figure: a card never shows a number the engine did not report.
  if (row === undefined || (metric !== "mastery" && metric !== "change")) return null;
  return (
    <li data-skill={row.skillId}>
      <SkillProgressFigure
        label={skillLabel(row.skillId, locale)}
        value={metric === "mastery" ? row.mastery : row.masteryChange}
        metric={metric}
        locale={locale}
      />
    </li>
  );
}

const ALERT_KINDS: readonly AlertKind[] = ["frustration", "plateau", "daily_cap", "safety"];

function AlertFigure({ props }: { props: WidgetProps }) {
  const figures = useContext(FiguresContext);
  const locale = useLocale();
  const kind = ALERT_KINDS.find((k) => k === readString(props, "reason"));
  if (figures === null || kind === undefined) return null;
  return (
    <li data-alert={kind}>
      <AlertCountFigure reason={kind} count={figures.alertCounts.get(kind) ?? 0} locale={locale} />
    </li>
  );
}

const TONES: readonly InsightTone[] = ["progress", "attention", "info"];

const Cards = defineComponent({
  name: "Cards",
  ...shared("Cards"),
  component: ({ props, renderNode }) => (
    <div className="parent-cards">{renderNode(props["cards"])}</div>
  ),
});

const InsightCard = defineComponent({
  name: "InsightCard",
  ...shared("InsightCard"),
  component: ({ props, renderNode }) => (
    <InsightCardView
      tone={TONES.find((tone) => tone === readString(props, "tone")) ?? "info"}
      title={readString(props, "title") ?? ""}
      body={readString(props, "body") ?? ""}
    >
      <ul className="parent-figures">{renderNode(props["figures"])}</ul>
    </InsightCardView>
  ),
});

const SkillProgress = defineComponent({
  name: "SkillProgress",
  ...shared("SkillProgress"),
  component: ({ props }) => <SkillFigure props={props} />,
});

const AlertCount = defineComponent({
  name: "AlertCount",
  ...shared("AlertCount"),
  component: ({ props }) => <AlertFigure props={props} />,
});

let library: ReturnType<typeof buildLibrary> | undefined;

function buildLibrary() {
  return createLibrary({
    id: parentInsightLibrary().id ?? "tutor-parent-insights",
    root: "Cards",
    components: [Cards, InsightCard, SkillProgress, AlertCount],
  });
}

/** The React side of the parent card library, built on first use. */
export function parentCardLibrary() {
  library ??= buildLibrary();
  return library;
}
